import React, { useRef, useEffect, useState, useCallback } from 'react'

export interface LogEntry {
  id: string
  message: string
  type: 'info' | 'success' | 'error' | 'warning'
  timestamp: Date
}

// Logger service functions that work with local state
export const createLoggerService = () => {
  let logs: LogEntry[] = []
  let isVisible = false
  let listeners: (() => void)[] = []

  const addLog = (message: string, type: LogEntry['type'] = 'info') => {
    console.log('addLog', message, type)
    const newLog: LogEntry = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      message,
      type,
      timestamp: new Date()
    }
    logs = [...logs, newLog]
    listeners.forEach((listener) => listener())
  }

  const clearLogs = () => {
    logs = []
    listeners.forEach((listener) => listener())
  }

  const toggleLogger = () => {
    isVisible = !isVisible
    listeners.forEach((listener) => listener())
  }

  const getLogs = () => logs
  const getLoggerVisibility = () => isVisible

  const subscribe = (listener: () => void) => {
    listeners.push(listener)
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  }

  return {
    addLog,
    clearLogs,
    toggleLogger,
    getLogs,
    getLoggerVisibility,
    subscribe
  }
}

// Create a single logger service instance that will be shared
const loggerService = createLoggerService()

// Main LoggerUI component that manages its own state
export const LoggerUI: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [isVisible, setIsVisible] = useState(false)
  const logsEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [logs])

  useEffect(() => {
    // Clear all logs on new mount
    loggerService.clearLogs()

    // Subscribe to logger service updates
    const unsubscribe = loggerService.subscribe(() => {
      setLogs(loggerService.getLogs())
      setIsVisible(loggerService.getLoggerVisibility())
    })

    return unsubscribe
  }, [loggerService])

  const handleToggle = () => {
    loggerService.toggleLogger()
  }

  const getLogTypeStyles = (type: LogEntry['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200'
    }
  }

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'success':
        return '✅'
      case 'error':
        return '❌'
      case 'warning':
        return '⚠️'
      default:
        return 'ℹ️'
    }
  }

  if (!isVisible) {
    return (
      <button
        onClick={handleToggle}
        className='fixed bottom-4 right-4 bg-gray-800 text-white p-3 rounded-full shadow-lg hover:bg-gray-700 transition-colors z-50'
      >
        📋
      </button>
    )
  }

  return (
    <div className='fixed bottom-4 right-4 w-80 h-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50'>
      {/* Header */}
      <div className='flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg'>
        <h3 className='font-semibold text-gray-800'>Debug Logger</h3>
        <button onClick={handleToggle} className='text-gray-500 hover:text-gray-700 transition-colors'>
          ✕
        </button>
      </div>

      {/* Logs Container */}
      <div className='h-80 overflow-y-auto p-3 space-y-2'>
        {logs.length === 0 ? (
          <div className='text-center text-gray-500 text-sm py-8'>No logs yet...</div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className={`p-2 rounded border text-xs ${getLogTypeStyles(log.type)}`}>
              <div className='flex items-start gap-2'>
                <span className='text-sm'>{getLogIcon(log.type)}</span>
                <div className='flex-1'>
                  <div className='font-medium'>{log.message}</div>
                  <div className='text-xs opacity-70 mt-1'>{log.timestamp.toLocaleTimeString()}</div>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>

      {/* Footer */}
      <div className='p-2 border-t border-gray-200 bg-gray-50 rounded-b-lg'>
        <div className='text-xs text-gray-500 text-center'>
          {logs.length} log{logs.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  )
}

// Export the logger service functions for external use
export const { addLog, clearLogs, toggleLogger } = loggerService

export default LoggerUI
